import { useState } from "react";
import { Users, Trash2, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import { RoleType } from "@/types/project";
import type { OrganizationUser } from "@/types/organization";
import { useRolesQuery } from "@/hooks/queries/useProjectUsersQuery";
import {
  useOrganizationUsersQuery,
  useUpdateOrganizationUserRoleMutation,
  useRemoveOrganizationUserMutation,
} from "@/hooks/queries/useOrganizationUsersQuery";
import {
  useOrganizationSeatSummaryQuery,
  useActivateSeatMutation,
  useDeactivateSeatMutation,
} from "@/hooks/queries/useSeatsQuery";

interface OrganizationUsersTabProps {
  organizationId: number;
}

const OrganizationUsersTab = ({ organizationId }: OrganizationUsersTabProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user, isLoading: authLoading } = useAuth();
  const currentUserId = Number(user?.id ?? 0);

  const { data: organizationUsers = [], isLoading, isError } = useOrganizationUsersQuery(organizationId);
  const { data: roles = [] } = useRolesQuery();

  const currentMembership = organizationUsers.find((ou) => ou.userId === currentUserId);
  const canManage = currentMembership?.roleType === RoleType.Owner || currentMembership?.roleType === RoleType.Editor;
  const permissionsLoading = authLoading || isLoading;

  const updateRoleMutation = useUpdateOrganizationUserRoleMutation();
  const removeMutation = useRemoveOrganizationUserMutation();

  const { data: seatSummary } = useOrganizationSeatSummaryQuery(organizationId);
  const activateSeatMutation = useActivateSeatMutation();
  const deactivateSeatMutation = useDeactivateSeatMutation();
  const seatsAvailableForActivation = (seatSummary?.availableSeats ?? 0) - (seatSummary?.activeSeats ?? 0);
  const seatStatusByUserId = new Map((seatSummary?.members ?? []).map((m) => [m.userId, m]));

  const handleSeatToggle = (userId: number, activate: boolean) => {
    if (activate) {
      activateSeatMutation.mutate({ organizationId, userId });
    } else {
      deactivateSeatMutation.mutate({ organizationId, userId });
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [userToRemove, setUserToRemove] = useState<OrganizationUser | null>(null);

  const getRoleLabel = (roleType: RoleType) => {
    switch (roleType) {
      case RoleType.Owner: return t("roleOwner");
      case RoleType.Editor: return t("roleEditor");
      case RoleType.Viewer: return t("projectViewer");
      default: return String(roleType);
    }
  };

  const getRoleBadgeVariant = (roleType: RoleType): "default" | "secondary" | "outline" => {
    switch (roleType) {
      case RoleType.Owner: return "default";
      case RoleType.Editor: return "secondary";
      case RoleType.Viewer: return "outline";
      default: return "outline";
    }
  };

  const filteredUsers = organizationUsers.filter((ou) =>
    ou.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ou.userNickName ?? "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRoleChange = (userId: number, newRoleId: string) => {
    updateRoleMutation.mutate({ organizationId, userId, roleId: Number(newRoleId) });
  };

  const handleConfirmRemove = () => {
    if (!userToRemove) return;
    removeMutation.mutate(
      { organizationId, userId: userToRemove.userId },
      { onSuccess: () => setUserToRemove(null) }
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t("totalMembers")}: {organizationUsers.length}</span>
        </div>
      </div>

      <Input placeholder={t("searchTeamMembers")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-sm" />

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : isError ? (
        <div className="text-center py-12 text-destructive text-sm">{t("organizationMembersLoadError")}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">{searchQuery ? t("noMembersFound") : t("noUsersAvailable")}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead className="w-40">{t("permission")}</TableHead>
                {canManage && <TableHead className="w-32">{t("seatStatus")}</TableHead>}
                {(canManage || permissionsLoading) && <TableHead className="w-16" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((ou) => {
                const isPending = !ou.invitationAcceptedAt;
                return (
                <TableRow key={ou.id}>
                  <TableCell className="font-medium">{ou.userNickName ?? <span className="text-muted-foreground italic">—</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{ou.userEmail}</TableCell>
                  <TableCell>
                    {isPending ? (
                      <Badge variant="outline">{t("pendingInvitationBadge")}</Badge>
                    ) : canManage && roles.length > 0 ? (
                      <Select value={String(ou.roleId)} onValueChange={(val) => handleRoleChange(ou.userId, val)} disabled={updateRoleMutation.isPending}>
                        <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => <SelectItem key={role.id} value={String(role.id)}>{getRoleLabel(role.roleType)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getRoleBadgeVariant(ou.roleType)}>{getRoleLabel(ou.roleType)}</Badge>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      {isPending ? (
                        <span className="text-xs text-muted-foreground">{t("pendingInvitationBadge")}</span>
                      ) : (() => {
                        const seatStatus = seatStatusByUserId.get(ou.userId);
                        const isSeatActive = seatStatus?.seatActive ?? false;
                        const isMutating = activateSeatMutation.isPending || deactivateSeatMutation.isPending;
                        const cannotActivate = !isSeatActive && seatsAvailableForActivation <= 0;
                        const seatSwitch = (
                          <Switch
                            checked={isSeatActive}
                            disabled={isMutating || cannotActivate}
                            onCheckedChange={(checked) => handleSeatToggle(ou.userId, checked)}
                            aria-label={isSeatActive ? t("deactivateSeat") : t("activateSeat")}
                          />
                        );
                        return (
                          <div className="flex items-center gap-2">
                            {cannotActivate ? (
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">{seatSwitch}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>{t("noSeatsAvailableTooltip")}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              seatSwitch
                            )}
                            <span className="text-xs text-muted-foreground">
                              {isSeatActive ? t("seatActive") : t("seatInactive")}
                            </span>
                          </div>
                        );
                      })()}
                    </TableCell>
                  )}
                  {(canManage || permissionsLoading) && (
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setUserToRemove(ou)} aria-label={t("removeMember")} disabled={permissionsLoading}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!userToRemove} onOpenChange={(open) => { if (!open) setUserToRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmRemoveUser")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmRemoveOrganizationUserDesc")}
              {userToRemove && <span className="block mt-1 font-medium text-foreground">{userToRemove.userEmail}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleConfirmRemove} disabled={removeMutation.isPending}>
              {removeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("removeMember")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrganizationUsersTab;
